import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Trash2, Check } from "lucide-react";
import { http, formatApiError } from "../resources/resourceUtils";

const inputCls = "w-full rounded-xl border px-4 py-2.5 text-[16px] bg-white focus:outline-none focus:border-[#8A9470]";

const CategoryRow = ({ c, i, total, count, onMove, onChanged }) => {
    const [name, setName] = useState(c.name);
    const run = async (fn, msg) => {
        try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <li className="flex items-center gap-3 py-3 border-b" style={{ borderColor: "#EEEBE3" }} data-testid={`admin-category-row-${c.id}`}>
            <div className="flex flex-col" style={{ color: "#8A8A73" }}>
                <button type="button" aria-label={`Move ${c.name} up`} disabled={i === 0} onClick={() => onMove(i, i - 1)} className="disabled:opacity-30" data-testid={`admin-category-up-${c.id}`}><ChevronUp size={15} /></button>
                <button type="button" aria-label={`Move ${c.name} down`} disabled={i === total - 1} onClick={() => onMove(i, i + 1)} className="disabled:opacity-30" data-testid={`admin-category-down-${c.id}`}><ChevronDown size={15} /></button>
            </div>
            <label htmlFor={`cat-${c.id}`} className="sr-only">Category name</label>
            <input id={`cat-${c.id}`} className={inputCls} style={{ borderColor: "#E8E4DA" }} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid={`admin-category-name-${c.id}`} />
            <span className="text-[14px] whitespace-nowrap w-20" style={{ color: "#7D7D68" }}>{count} item{count === 1 ? "" : "s"}</span>
            <button type="button" aria-label="Save name" title="Save name" disabled={!name.trim() || name.trim() === c.name}
                onClick={() => run(() => http.put(`/admin/categories/${c.id}`, { name }), "Category renamed")}
                className="p-2 rounded-lg hover:bg-[#F1EFE8] disabled:opacity-30" data-testid={`admin-category-save-${c.id}`}><Check size={16} /></button>
            <button type="button" aria-label={`Delete ${c.name}`} title="Delete"
                onClick={() => { if (window.confirm(`Delete the category "${c.name}"?`)) run(() => http.delete(`/admin/categories/${c.id}`), "Category deleted"); }}
                className="p-2 rounded-lg hover:bg-[#F1EFE8]" data-testid={`admin-category-delete-${c.id}`}><Trash2 size={16} style={{ color: "#9A4557" }} /></button>
        </li>
    );
};

export const CategoryManager = ({ categories, resources, onChanged }) => {
    const [name, setName] = useState("");
    const add = async (e) => {
        e.preventDefault();
        try { await http.post("/admin/categories", { name }); setName(""); toast.success("Category added"); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
    };
    const onMove = async (from, to) => {
        const ids = categories.map((c) => c.id);
        const [m] = ids.splice(from, 1);
        ids.splice(to, 0, m);
        try { await http.post("/admin/categories/reorder", { ids }); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <div className="max-w-2xl" data-testid="admin-categories">
            <p className="text-[16px] leading-[1.7]" style={{ color: "#5C5C47" }}>
                Categories appear as filters on the Resources page in this order. A category can be deleted once it has no resources.
            </p>
            <ul className="mt-6">
                {categories.map((c, i) => (
                    <CategoryRow key={`${c.id}-${c.name}`} c={c} i={i} total={categories.length} onMove={onMove} onChanged={onChanged}
                        count={resources.filter((r) => r.category_id === c.id).length} />
                ))}
            </ul>
            <form onSubmit={add} className="mt-6 flex gap-3">
                <label htmlFor="new-category" className="sr-only">New category name</label>
                <input id="new-category" className={inputCls} style={{ borderColor: "#E8E4DA" }} placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid="admin-category-new-input" />
                <button type="submit" disabled={!name.trim()} className="res-btn whitespace-nowrap disabled:opacity-60" data-testid="admin-category-add">Add category</button>
            </form>
        </div>
    );
};
